import { useEffect, useMemo, useState } from 'react';
import { Button, InputAdornment, TextField } from '@mui/material';
import { BaseDialog } from '../common';

type RenameDialogProps = {
  open: boolean;
  currentName: string;
  lockExtension?: boolean;
  onClose: () => void;
  onSubmit: (newName: string) => void;
};

const splitNameAndExtension = (
  fullName: string,
  lockExtension: boolean
): { baseName: string; extension: string } => {
  if (!lockExtension) {
    return { baseName: fullName, extension: '' };
  }
  const lastDot = fullName.lastIndexOf('.');
  // A leading dot (e.g. ".gitignore") is treated as a hidden file with no extension to lock.
  if (lastDot <= 0 || lastDot === fullName.length - 1) {
    return { baseName: fullName, extension: '' };
  }
  return {
    baseName: fullName.slice(0, lastDot),
    extension: fullName.slice(lastDot),
  };
};

export const RenameDialog = ({
  open,
  currentName,
  lockExtension = false,
  onClose,
  onSubmit,
}: RenameDialogProps) => {
  const { baseName: initialBaseName, extension } = useMemo(
    () => splitNameAndExtension(currentName, lockExtension),
    [currentName, lockExtension]
  );

  const [baseName, setBaseName] = useState(initialBaseName);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) setBaseName(initialBaseName);
  }, [initialBaseName, open]);

  const trimmedBase = baseName.trim();
  const composedName = trimmedBase + extension;
  const isDisabled = !trimmedBase || composedName === currentName;

  const handleSubmit = () => {
    if (isDisabled) return;
    onSubmit(composedName);
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Rename item"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isDisabled}>
            Save
          </Button>
        </>
      }
    >
      <TextField
        autoFocus
        fullWidth
        size="small"
        label="Name"
        value={baseName}
        onChange={(e) => setBaseName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
        }}
        InputProps={
          extension
            ? {
                endAdornment: <InputAdornment position="end">{extension}</InputAdornment>,
              }
            : undefined
        }
        sx={{ mt: 0.5 }}
      />
    </BaseDialog>
  );
};
